import { useRef, useState } from "react";
import type { TranslateFn } from "../../i18n";
import { compressImage, isValidImageFile } from "../../image-utils";
import type { Profile } from "../types";
import { normalizeProfilePhotoSource } from "../utils/profile-image";

const MALE_FIELDS = [
  { key: "heightCm", label: "Height", metricUnit: "cm", imperialUnit: "ft/in" },
  { key: "weightKg", label: "Weight", metricUnit: "kg", imperialUnit: "lbs" },
  { key: "age", label: "Age", metricUnit: "years", imperialUnit: "years" },
];

const FEMALE_FIELDS = [
  { key: "heightCm", label: "Height", metricUnit: "cm", imperialUnit: "ft/in" },
  { key: "weightKg", label: "Weight", metricUnit: "kg", imperialUnit: "lbs" },
  { key: "age", label: "Age", metricUnit: "years", imperialUnit: "years" },
];

export function ProfileEditView({
  profile, onSave, onCancel, t,
}: {
  profile?: Profile | null;
  onSave: (p: Omit<Profile, "id" | "createdAt">) => void;
  onCancel: () => void;
  t: TranslateFn;
}) {
  const [name, setName] = useState(profile?.name || "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const initialPhoto = normalizeProfilePhotoSource(profile?.photoUrl || profile?.photoBase64);
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialPhoto);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [gender, setGender] = useState<"male" | "female">(
    profile?.gender === "female" ? "female" : "male",
  );
  const [unit, setUnit] = useState<"cm" | "in">(profile?.sizingUnit as "cm" | "in" || "cm");
  const [vals, setVals] = useState<Record<string, string>>(() => {
    if (!profile) return {};
    const r: Record<string, string> = {};
    for (const k of ["heightCm", "weightKg", "age"]) {
      const v = (profile as unknown as Record<string, unknown>)[k];
      if (typeof v === "number" && v > 0) r[k] = String(v);
    }
    return r;
  });

  const fields = gender === "female" ? FEMALE_FIELDS : MALE_FIELDS;
  const [error, setError] = useState("");

  const handlePhotoSelect = async (file: File | null | undefined) => {
    if (!file) return;
    if (!isValidImageFile(file)) {
      setError(t("Please upload an image file"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t("Image must be under 10MB"));
      return;
    }

    setError("");
    setPhotoBusy(true);
    try {
      const compressed = await compressImage(file, { maxDimension: 1280, quality: 0.92 });
      setPhotoPreview(compressed);
      setPhotoBase64(compressed);
      setPhotoRemoved(false);
    } catch {
      setError(t("Failed to process image"));
    } finally {
      setPhotoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setPhotoBase64(null);
    setPhotoRemoved(true);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = () => {
    const missing: string[] = [];
    if (!name.trim()) missing.push(t("Profile Name"));
    for (const f of fields) {
      const v = parseFloat(vals[f.key] || "");
      if (!(v > 0)) missing.push(t(f.label));
    }
    if (missing.length > 0) {
      setError(t("Please fill in all fields"));
      return;
    }
    setError("");
    const p: Record<string, unknown> = { name: name.trim(), gender, sizingUnit: unit };
    for (const f of fields) {
      p[f.key] = parseFloat(vals[f.key]);
    }
    if (photoBase64) {
      p.photoBase64 = photoBase64;
      p.photoUrl = undefined;
    } else if (photoRemoved) {
      p.photoBase64 = undefined;
      p.photoUrl = undefined;
      p.profilePhotoRemoved = true;
    }
    onSave(p as Omit<Profile, "id" | "createdAt">);
  };

  return (
    <div className="ps-tryon-profile-edit">
      <div className="ps-tryon-pe-photo-row">
        <div className="ps-tryon-pe-photo-preview">
          {photoPreview ? (
            <img src={photoPreview} alt={t("Profile photo")} />
          ) : (
            <span>{t("No photo")}</span>
          )}
        </div>
        <div className="ps-tryon-pe-photo-actions">
          <button
            type="button"
            className="ps-tryon-pe-photo-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={photoBusy}
          >
            {photoBusy ? t("Processing…") : photoPreview ? t("Change Photo") : t("Add Photo")}
          </button>
          {photoPreview ? (
            <button type="button" className="ps-tryon-pe-photo-remove" onClick={handleRemovePhoto} disabled={photoBusy}>
              {t("Remove")}
            </button>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            style={{ display: "none" }}
            onChange={(event) => void handlePhotoSelect(event.target.files?.[0])}
          />
        </div>
      </div>

      {/* Name */}
      <div className="ps-tryon-sf-float-field">
        <label className="ps-tryon-sf-float-label">{t("Profile Name")}</label>
        <input
          type="text" className="ps-tryon-sf-input"
          value={name} onChange={e => { setName(e.target.value); setError(""); }}
          placeholder={t("e.g. My Measurements")}
        />
      </div>

      {/* Gender + Unit inline row */}
      <div className="ps-tryon-pe-row">
        <div className="ps-tryon-pe-pill">
          <button
            type="button"
            className={`ps-tryon-pe-pill-btn${gender === "male" ? " ps-active" : ""}`}
            onClick={() => setGender("male")}
          >{t("Male")}</button>
          <button
            type="button"
            className={`ps-tryon-pe-pill-btn${gender === "female" ? " ps-active" : ""}`}
            onClick={() => setGender("female")}
          >{t("Female")}</button>
        </div>
        <div className="ps-tryon-pe-pill">
          <button
            type="button"
            className={`ps-tryon-pe-pill-btn${unit === "cm" ? " ps-active" : ""}`}
            onClick={() => setUnit("cm")}
          >cm</button>
          <button
            type="button"
            className={`ps-tryon-pe-pill-btn${unit === "in" ? " ps-active" : ""}`}
            onClick={() => setUnit("in")}
          >in</button>
        </div>
      </div>

      {/* Measurement fields */}
      <div className="ps-tryon-pe-fields">
        {fields.map(f => (
          <div key={f.key} className="ps-tryon-sf-float-field">
            <label className="ps-tryon-sf-float-label">{t(f.label)} ({unit === "in" ? f.imperialUnit : f.metricUnit})</label>
            <input
              type="number" className="ps-tryon-sf-input"
              placeholder=" "
              value={vals[f.key] || ""}
              onChange={e => { setVals(prev => ({ ...prev, [f.key]: e.target.value })); setError(""); }}
            />
          </div>
        ))}
      </div>

      {/* Error */}
      {error && <div className="ps-tryon-pe-error">{error}</div>}

      {/* Actions */}
      <div className="ps-tryon-pe-actions">
        <button type="button" className="ps-tryon-est-back" onClick={onCancel}>{t("Cancel")}</button>
        <button type="button" className="ps-tryon-pe-save" onClick={handleSave}>
          {profile ? t("Save Changes") : t("Create Profile")}
        </button>
      </div>
    </div>
  );
}
